import type { AttendEventResponse, RegistrationPhase } from '@models/api';
import { getEventTicketLink } from '@utils/eventTicketLink';

/**
 * Where the member tapped Going: the event page, a live space slot, the post-sign-in replay, or the
 * attend prompt that asks before a post from an Event (TECH-456).
 */
export type RegistrationSurface = 'event_page' | 'live_slot' | 'post_auth' | 'event_post_prompt';

const SURFACES: Record<RegistrationSurface, true> = {
  event_page: true,
  live_slot: true,
  post_auth: true,
  event_post_prompt: true,
};

export function isRegistrationSurface(value: unknown): value is RegistrationSurface {
  return typeof value === 'string' && Object.hasOwn(SURFACES, value);
}

const PHASES: Record<RegistrationPhase, true> = { upcoming: true, live: true };

/**
 * What the ticketed Going celebration (`registration_check`) needs, carried on the celebration
 * intent so it survives the live slot navigating away and a post-sign-in reload. The domain is not
 * carried: it is derived from `registration_url` where it renders (`registrationDomain`), so the
 * drawer can never name one site and open another.
 */
export interface RegistrationCelebration {
  event_id: string;
  /** The ticket link as the server returned it, opened unchanged: it can carry a partner code. */
  registration_url: string;
  phase: RegistrationPhase;
  surface: RegistrationSurface;
}

/**
 * Shape check for a stored payload: a malformed one must never reach the drawer. A `domain` key,
 * which the previous build stored beside the link, is ignored, so an intent written before a deploy
 * still shows after it.
 */
export function isRegistrationCelebration(value: unknown): value is RegistrationCelebration {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.event_id === 'string' &&
    v.event_id !== '' &&
    typeof v.registration_url === 'string' &&
    getEventTicketLink([v.registration_url]) !== null &&
    typeof v.phase === 'string' &&
    Object.hasOwn(PHASES, v.phase) &&
    isRegistrationSurface(v.surface)
  );
}

/**
 * The ticketed celebration for a Going response, or null when the member should not be asked.
 *
 * The server decides (`ask_registration`); the client does no date, role or mode logic. A
 * response that asks but carries no usable link or phase also yields null, so the caller falls
 * back to the ordinary Going flow instead of rendering a drawer with nothing to link to.
 */
export function buildRegistrationCelebration(
  response: AttendEventResponse,
  surface: RegistrationSurface,
): RegistrationCelebration | null {
  if (!response.ask_registration) return null;
  const phase = response.registration_phase;
  if (phase !== 'upcoming' && phase !== 'live') return null;
  const link = getEventTicketLink(response.registration_url ? [response.registration_url] : []);
  if (!link) return null;
  return {
    event_id: response.event_id,
    registration_url: link.url,
    phase,
    surface,
  };
}

/** The ticket site as people see it: the host of `registration_url`, without a leading "www.". */
export function registrationDomain(registration: RegistrationCelebration): string {
  // Both producers of a RegistrationCelebration (the builder above, and the shape check for a
  // stored one) require a usable link, so the fallback only keeps the type honest.
  return (
    getEventTicketLink([registration.registration_url])?.domain ?? registration.registration_url
  );
}
